import { useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { formatDateTime, relativeTime } from '@/lib/format'
import { describeInterval } from '@/settings/uptime/intervals'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type Site = Schema<'SiteListItemOut'>
type Monitor = Schema<'MonitorOut'>
type Check = Schema<'CheckOut'>

/** The tenant's time zone is the only one this product renders in (CLAUDE.md). */
const IST = 'Asia/Kolkata'

/*
 * The uptime interval vocabulary — `INTERVALS`, `OFF`, `PAUSED`, `optionsFor`,
 * `describeInterval` — lived here until the settings consolidation's second
 * batch (2026-09-15). The control that used them is `/settings/uptime` now
 * (`settings/uptime/`); this page shows the choice as text in the same column.
 */

/**
 * Sites — `design/screens/seo.html#sites`, audit row §A3, built in S-G6's
 * successor over `api/app/v1/uptime.py`.
 *
 * S-B30 shipped the pinger, the state machine and all three tenant-facing
 * routes on 2026-08-16 and the sidebar has rendered *Sites* disabled ever
 * since. This is the screen those routes were built for, and it is the last of
 * `navigation.ts`'s stale entries.
 *
 * WHY THE MOCKUP'S ONE INTERVAL SELECT BECAME ONE PER ROW
 * --------------------------------------------------------
 * The prototype draws a single *Check interval* select in the uptime card's
 * footer, which reads as an organisation-wide setting. It is not one:
 * `uptime_monitors.interval_seconds` is per monitor and a monitor is per site,
 * for the same reason `run_schedules.site_id` is unique — an agency running a
 * staging domain beside a client's live domain wants different answers. So the
 * control moved into the row it actually governs, and it is the *only* control
 * on this screen because the three routes are one decision at three settings:
 * `DELETE` is *not watched*, `PUT enabled: false` is *paused*, and `PUT` with an
 * interval is *watched this often*. A separate Start/Stop pair beside a select
 * would let the tenant press *Stop* on a monitor whose interval they had just
 * changed and wonder which of the two had happened.
 *
 * **Paused is offered rather than folded into *not watched***, although both
 * stop the pings. Deleting the monitor discards its history and its interval;
 * pausing keeps both, which is what somebody doing a migration at midnight
 * wants — and `test_a_paused_monitor_is_not_swept` says the server means it.
 *
 * WHY THE COLUMN SAYS "LAST 24 HOURS" AND NOT "30-DAY UPTIME"
 * ------------------------------------------------------------
 * Because a 30-day figure does not exist. `UptimeTransaction.history` returns
 * `HISTORY_LIMIT` rows — 288, one day at the fastest interval — and nothing
 * aggregates beyond that window, so the prototype's *99.97%* would have to be
 * computed from a month AugRank does not keep. This is the same call §V2 made
 * about the prototype's ₹4.20: the honest answer is the window that exists,
 * labelled as that window, rather than a plausible number with nothing behind
 * it. The caption under the table says so in as many words.
 *
 * WHAT THIS SCREEN DOES NOT WRITE, AND WHY THE CARDS STILL OFFER IT
 * -------------------------------------------------------------------
 * **Add site is a link, not a form.** The only writer of a site is
 * `PUT /api/v1/onboarding/site`, which also settles wizard step 1 and takes a
 * business description and an approval-gating choice — a form the prototype does
 * not draw. So the header and the empty state both send a tenant to step 1,
 * which is where a site is added.
 *
 * **The quota beside it is real** (SEO-UI-6). This file argued for two sessions
 * that the prototype's *"Add site — 1 left on Growth"* implied a per-plan site
 * quota "and there is none in the model", and that was simply wrong:
 * `app/billing/plans.Plan.sites` has been 1 / 3 / 10 since the plans were
 * written, and `SITES_KEY` puts it in `subscriptions.entitlements` beside the
 * seat and keyword ceilings. `GET /seo/sites` now carries it. See `AddSite`.
 *
 * **Run audit now is a link too** (SEO-UI-8). The button that *posts* the run
 * lives on `/seo/audit`, over the endpoint that owns it, and a second writer
 * here would be a second place to look when it goes wrong. What the card
 * carries is the site: `/seo/audit?site={id}` opens that screen on this card's
 * domain, which is what makes a per-site card's action per-site.
 *
 * **Check again is the one write** (SEO-UI-9). Re-verification has no screen of
 * its own and the card is the only place the question is asked, so
 * `POST /seo/sites/{id}/verify` is called from here — see `Recheck` on why it is
 * on the unverified cards only, and `app/seo/service.reverify` on why AugRank
 * asks Google rather than growing a DNS TXT record the prototype draws.
 *
 * **Remove is deliberately absent**, and it is the one row of §A3 that is not a
 * drawing to be corrected. A site is the parent of its audits, its findings, its
 * keywords, its rank history, its content, its monitor and its schedule, and
 * every one of those is `ON DELETE CASCADE`. A quiet ghost button in a card
 * footer is the wrong control for an irreversible deletion of a year of a
 * tenant's measurements; it wants a confirmation that names what goes with it,
 * and there is no route to call yet either. It is a feature, not a parity fix.
 *
 * **The monitor's URL as an editable field.** `MonitorIn.url` is required and
 * this screen derives it — `https://{domain}`, the same origin
 * `app/seo/sitemap.origin_of` and `app/seo/publish.py` both build — and an
 * existing monitor keeps whatever URL it already has. A box here would let a row
 * headed *kavyainteriors.in* report a different site's health, which is the one
 * thing an uptime table must never do.
 */
export function Sites(): JSX.Element {
  const brand = useBrand()
  const { membership } = useActiveTenant()
  // `discovery:manage` is what `app/v1/uptime.py` puts on `PUT` and `DELETE`,
  // on the line it draws in its own comment: operating the product rather than
  // purchasing it. Reading is `tenant:read`, which every seat holds.
  const manageable = may(membership, 'discovery:manage')

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])

  if (sites.state.status === 'loading') return <Loading label="Loading your sites" />
  if (sites.state.status === 'error') {
    return <ErrorState failure={sites.state.failure} onRetry={sites.refetch} />
  }

  const listed = sites.state.data.sites

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row">
        <h1 className="ar-h1">Sites</h1>
        <span className="app-spacer" />
        <AddSite count={listed.length} limit={sites.state.data.site_limit ?? null} />
      </div>

      {listed.length === 0 ? (
        <div className="ar-card">
          <div className="ar-card__body">
            <Empty
              title="No site yet"
              body={`${brand.productName} has nothing to audit, rank or watch until it knows where your pages live. Adding your website is the first step of setup — finish it and this page fills in.`}
              action={
                <a className="ar-btn ar-btn--primary ar-btn--sm" href="/onboarding/site">
                  Add your website
                </a>
              }
            />
          </div>
        </div>
      ) : (
        <>
          <div className="ar-grid ar-grid--2">
            {listed.map((site) => (
              <SiteCard
                key={site.id}
                site={site}
                manageable={manageable}
                onVerified={sites.refetch}
              />
            ))}
          </div>
          <UptimeCard sites={listed} />
        </>
      )}
    </div>
  )
}

/**
 * SEO-UI-6, SEO-UI-33. The header's primary action, and the plan's ceiling on it.
 *
 * **The quota is real now, and the argument that said otherwise was wrong.**
 * This file used to state that the prototype's *"Add site — 1 left on Growth"*
 * implied a per-plan site quota "and there is none in the model". There is:
 * `app/billing/plans.Plan.sites` is 1 on Starter, 3 on Growth and 10 on Scale,
 * and `SITES_KEY` puts it in `subscriptions.entitlements` beside the seat and
 * keyword limits. `GET /seo/sites` now carries it, exactly as the wizard's
 * keyword badge carries `keyword_limit` (ONB-14).
 *
 * **Displayed, not enforced here**, and the two are different promises. The only
 * writer of a site is `PUT /api/v1/onboarding/site`, so this button is a link to
 * step 1 rather than a form — which is also why a tenant at their ceiling still
 * gets a control: the honest thing is to say what the plan allows and let them
 * see the upgrade, not to render a dead button with no explanation attached.
 *
 * A plan that states no ceiling — no live subscription, or a null entitlement —
 * draws the bare count this header has always shown. An invented quota in front
 * of a paying tenant is worse than no quota at all.
 */
function AddSite({ count, limit }: { count: number; limit: number | null }): JSX.Element {
  if (limit === null) {
    return (
      <span className="ar-subtle ar-text-body-sm">
        {count === 1 ? '1 site' : `${String(count)} sites`}
      </span>
    )
  }

  const left = limit - count
  if (left <= 0) {
    return (
      <div className="ar-btn-group">
        <span className="ar-badge ar-badge--warning">
          {count} of {limit} sites
        </span>
        <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/billing/plans">
          Change plan
        </Link>
      </div>
    )
  }

  return (
    <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/onboarding/site">
      Add site — {left} left
    </Link>
  )
}

/**
 * SEO-UI-7. A publisher's stored key as the name the tenant chose it by.
 *
 * The four ids `app/seo/publish.py` dispatches on, and the labels
 * `PublishTargets.tsx` already gives them — the same string in two places on two
 * screens has to read the same way in both. Anything unrecognised falls through
 * to the raw key rather than to "Unknown": an operator who put a fifth value on
 * the row is better served by seeing it than by being told it is nothing.
 */
const TARGETS: Record<string, string> = {
  git: 'Git repository',
  wordpress: 'WordPress',
  webflow: 'Webflow',
  shopify: 'Shopify',
  // Was absent, so the row printed the enum (walkthrough 2026-09-12).
  download: 'Files to download',
}

/* --- one site ------------------------------------------------------------------- */

/** What each `verification_status` means, in the tenant's words. */
const VERIFICATION: Record<string, { tone: 'success' | 'warning' | 'danger'; label: string }> = {
  verified: { tone: 'success', label: '✓ Verified' },
  no_access: { tone: 'danger', label: '⚠ No access' },
  unverified: { tone: 'warning', label: '⚠ Not verified' },
}

/** The sentence under the table, when there is something to explain. */
const VERIFICATION_NOTE: Record<string, string> = {
  no_access:
    'The connected Google account has no Search Console property for this domain. Connect a different account, or ask whoever manages the site to give you access.',
  unverified:
    'This domain has not been matched to a Search Console property yet. Until it is, rank and search figures for this site stay empty — uptime below does not need it.',
}

/**
 * One site: what Google resolved it to, and what state it is in.
 *
 * **Facts only, and no query of its own.** Everything here — the five
 * operational figures included — arrives in the one `GET /seo/sites` the page
 * already made, which since SEO-UI-7 answers them from three grouped aggregates
 * rather than from a request per card. The uptime figures are the table below
 * rather than a line in each card, because the question they answer — *which of
 * my sites is down* — is a comparison, and a comparison spread across cards is
 * one the reader has to do themselves.
 *
 * **The card links; it does not act.** *Run audit now* and *When AugRank runs*
 * both carry the site in the URL and open the screen that owns the write, for
 * the reason this file's own docstring gives about not growing a second writer
 * of the audit. *Check again* is the one exception and it is a genuine
 * exception: re-verification has no screen of its own, and the card is the only
 * place the question is asked.
 */
function SiteCard({
  site,
  manageable,
  onVerified,
}: {
  site: Site
  manageable: boolean
  /** Re-read the list once a re-check has changed this site's status. */
  onVerified: () => void
}): JSX.Element {
  const brand = useBrand()
  const badge = VERIFICATION[site.verification_status] ?? {
    tone: 'warning' as const,
    label: site.verification_status,
  }
  const note = VERIFICATION_NOTE[site.verification_status]
  const verified = site.verification_status === 'verified'

  return (
    <section className="ar-card" aria-label={site.domain}>
      <div className="ar-card__header">
        <h2>{site.domain}</h2>
        <span className="ar-card__spacer" />
        <span className={`ar-badge ar-badge--${badge.tone}`}>{badge.label}</span>
      </div>
      <div className="ar-card__body ar-stack">
        {/* SEO-UI-7. The five facts `seo.html:210-214` draws, in its order.
            **The Search Console and Analytics property strings moved out of the
            card**: they are the settings that produce these figures, not the
            figures, and they are read on the property picker where they are
            chosen. What a tenant opens this screen to learn is how much of the
            site has been looked at and how much is outstanding. The verification
            badge and the note below still say when a property is the reason a
            row is empty. */}
        <table className="ar-table app-sites__facts">
          <tbody>
            <tr>
              <td>Pages crawled</td>
              <td className="ar-num">
                {site.pages_crawled === null || site.pages_crawled === undefined ? (
                  <span className="ar-muted">Not audited yet</span>
                ) : (
                  site.pages_crawled.toLocaleString('en-IN')
                )}
              </td>
            </tr>
            <tr>
              <td>Keywords tracked</td>
              <td className="ar-num">{site.keywords_tracked.toLocaleString('en-IN')}</td>
            </tr>
            <tr>
              <td>Open issues</td>
              <td className="ar-num">
                {/* Tone is the count's, not the row's: nought open issues is
                    good news and a warning badge on it would read as a problem
                    with no name. */}
                <span
                  className={`ar-badge ar-badge--${site.open_issues === 0 ? 'success' : 'warning'}`}
                >
                  {site.open_issues.toLocaleString('en-IN')}
                </span>
              </td>
            </tr>
            <tr>
              <td>Last audit</td>
              <td className="ar-num ar-muted">
                {site.last_audit_at === null || site.last_audit_at === undefined
                  ? 'Never'
                  : relativeTime(site.last_audit_at, new Date())}
              </td>
            </tr>
            <tr>
              <td>Publish target</td>
              <td className="ar-num">
                {site.publish_target === null || site.publish_target === undefined ? (
                  <Link className="ar-link" to="/seo/publish-targets">
                    Not set up
                  </Link>
                ) : (
                  describeTarget(site.publish_target)
                )}
              </td>
            </tr>
          </tbody>
        </table>
        {note ? (
          <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
            {note}
          </p>
        ) : null}
        {verified ? null : <Recheck site={site} manageable={manageable} onVerified={onVerified} />}
      </div>
      {/* SHELL-13 and SEO-UI-8. *Run triggers* left the sidebar in SF-18 — S-D3
          approved eight SEO sections and it was a tenth — so this card is where
          it is reached from now. That is also where it belongs: the schedule and
          its conditions are set **per site**, which is the argument this file's
          own docstring already made about not duplicating the button.

          **The site travels with both links.** `/seo/triggers` and `/seo/audit`
          each open on the first site they are given, so a bare link from the
          second card would show the first card's schedule, or the first card's
          findings, under a heading naming this one — the same confusion the
          uptime section refuses by deriving its URL rather than taking one. */}
      <div className="ar-card__footer">
        <Link
          className="ar-btn ar-btn--ghost ar-btn--sm"
          to={`/seo/audit?site=${encodeURIComponent(site.id)}`}
        >
          Run audit now
        </Link>
        <span className="ar-card__spacer" />
        <Link
          className="ar-btn ar-btn--quiet ar-btn--sm"
          to={`/seo/triggers?site=${encodeURIComponent(site.id)}`}
        >
          When {brand.productName} runs
        </Link>
      </div>
    </section>
  )
}

/**
 * SEO-UI-9's *Check again*, on the cards that have something to re-check.
 *
 * **Only on an unverified or no-access card**, because a verified site has
 * nothing to ask about: the property is matched and the button would either do
 * nothing visible or, on a bad day at Google, move a working site to
 * `no_access`. The prototype draws it on the unverified card only and that is
 * the right place.
 *
 * The success toast names the outcome rather than the action. *"Checked"* is
 * true of both answers and tells the tenant nothing; what they pressed the
 * button to learn is whether Google can see their property yet.
 */
function Recheck({
  site,
  manageable,
  onVerified,
}: {
  site: Site
  manageable: boolean
  onVerified: () => void
}): JSX.Element | null {
  const toast = useToast()
  const [failure, setFailure] = useState<string | null>(null)
  const recheck = useMutation(() =>
    call(() =>
      api.POST('/api/v1/seo/sites/{site_id}/verify', {
        params: { path: { site_id: site.id } },
      }),
    ),
  )

  // A member can read this screen and cannot write it. Rendering the button
  // disabled would advertise a control the seat will never be able to press.
  if (!manageable) return null

  return (
    <div className="ar-stack ar-stack--tight">
      <div>
        <Button
          size="sm"
          disabled={recheck.pending}
          onClick={() => {
            setFailure(null)
            void recheck.run(undefined).then((result) => {
              if (!result.ok) {
                setFailure(result.failure.message)
                return
              }
              const matched = result.data.verification_status === 'verified'
              // **The list is re-read, not just toasted about.** Without this the
              // card goes on showing *⚠ Not verified*, its explanatory note and
              // this very button, directly under a toast saying the site is
              // verified — the screen contradicting itself until a reload. It is
              // the same reason `UptimeRow.choose` refetches after every one of
              // its three routes: the row has to describe the row, not the
              // request. `verified_at` and the badge both come off the list, so
              // there is nothing useful to patch in locally from the response.
              onVerified()
              toast.show({
                kind: matched ? 'success' : 'info',
                title: matched
                  ? `${site.domain} is verified`
                  : `Google still has no property for ${site.domain}`,
                body: matched
                  ? 'Its rank and search figures will fill in from the next run.'
                  : 'Add this Google account to the property in Search Console, then check again.',
              })
            })
          }}
        >
          {recheck.pending ? 'Checking…' : 'Check again'}
        </Button>
      </div>
      {failure ? (
        <div className="app-sites__error" role="alert">
          {failure}
        </div>
      ) : null}
    </div>
  )
}

/** A publisher's stored key as the name the tenant chose it by. */
function describeTarget(target: string): string {
  return TARGETS[target] ?? target
}

/* --- uptime --------------------------------------------------------------------- */

/**
 * Every site's uptime, one row each.
 *
 * The copy in the body and the footer is the prototype's, with one correction
 * it could not have known about: it says *"two consecutive failures email
 * you"*, and `app/seo/uptime.py` fixed the threshold at **one** because S-B30's
 * exit criterion is detection *within one ping interval* and two failures would
 * make that two intervals. The recovery is symmetric for the same reason, and
 * the notification carries a per-transition dedupe key so a flapping site sends
 * one notice per episode rather than one per ping. Saying "two" here would have
 * a tenant waiting for a second failure that is never counted.
 */
function UptimeCard({ sites }: { sites: Site[] }): JSX.Element {
  return (
    <section className="ar-card" aria-label="Uptime">
      <div className="ar-card__header">
        <h2>Uptime</h2>
        <span className="ar-card__spacer" />
        <span className="ar-badge ar-badge--neutral">Checked from outside AWS</span>
        <Link
          className="ar-btn ar-btn--ghost ar-btn--sm"
          to={
            sites.length === 1 ? `/settings/uptime?site=${sites[0]?.id ?? ''}` : '/settings/uptime'
          }
        >
          Change in Settings →
        </Link>
      </div>
      {/* SEO-UI-31, SEO-UI-32. `ar-stack` rather than `ar-stack--tight`, on both
          this card and the site cards above. `seo.html:207` and `:247` both put
          `var(--ar-space-4)` between a card body's blocks; `--tight` is
          `var(--ar-space-2)`, and at half the gap the intro paragraph, the table
          and the two captions below it read as one undifferentiated column of
          small grey text rather than as an explanation, a table and its notes. */}
      <div className="ar-card__body ar-stack">
        <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
          These checks are deliberately dumb — a plain request from outside AWS on a fixed interval,
          with no model in the loop. A monitor that can reason about whether your site is down is a
          monitor that can talk itself out of paging you.
        </p>
        <div className="ar-table-wrap" tabIndex={0}>
          <table className="ar-table">
            <thead>
              <tr>
                <th>Site</th>
                <th>Status</th>
                <th className="ar-num">Last 24 h</th>
                <th className="ar-num">Last check</th>
                <th>Last incident</th>
                <th>
                  <span className="ar-visually-hidden">Checks</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => (
                <UptimeRow key={site.id} site={site} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="ar-card__footer">
        <span className="ar-subtle ar-text-body-sm">
          One failed check takes a site down and emails you; one success brings it back. We never
          open a support ticket on your behalf, and we never change your site because it was down.
        </span>
      </div>
    </section>
  )
}

/**
 * One site's row, and the choice that configures it — read here, edited on
 * `/settings/uptime`.
 *
 * Its own query rather than one request for the whole table, because there is
 * no endpoint for the whole table: `GET /seo/sites/{id}/uptime` is per site and
 * a monitor is per site. One component per row is what makes that N requests
 * fired together instead of N awaited in sequence, and what lets a row that
 * fails say so without taking the other rows' figures off the screen.
 */
function UptimeRow({ site }: { site: Site }): JSX.Element {
  const brand = useBrand()
  const now = new Date()

  const uptime = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/sites/{site_id}/uptime', {
          params: { path: { site_id: site.id } },
          signal,
        }),
      ),
    [site.id],
  )

  const domain = <td className="ar-mono app-sites__domain">{site.domain}</td>

  if (uptime.state.status === 'loading') {
    return (
      <tr>
        {domain}
        <td className="ar-muted" colSpan={5} aria-busy="true">
          Reading this site’s checks…
        </td>
      </tr>
    )
  }

  if (uptime.state.status === 'error') {
    return (
      <tr>
        {domain}
        <td colSpan={5}>
          <span className="app-sites__error" role="alert">
            {uptime.state.failure.message}
          </span>{' '}
          <Button size="sm" onClick={uptime.refetch}>
            Try again
          </Button>
        </td>
      </tr>
    )
  }

  const monitor = uptime.state.data.monitor
  const history = uptime.state.data.history ?? []

  const control = (
    <td>
      <Link
        className="ar-link"
        to={`/settings/uptime?site=${site.id}`}
        aria-label={`Uptime checks for ${site.domain}`}
      >
        {monitor === null
          ? 'Off'
          : monitor.enabled
            ? describeInterval(monitor.interval_seconds)
            : 'Paused'}
      </Link>
    </td>
  )

  if (monitor === null) {
    return (
      <tr>
        {domain}
        <td>
          <span className="ar-badge ar-badge--neutral">Not watched</span>
        </td>
        <td className="ar-muted" colSpan={3}>
          {brand.productName} is not checking this site.
        </td>
        {control}
      </tr>
    )
  }

  const share = uptimeShare(history)

  return (
    <tr>
      {domain}
      <td>
        <StatusBadge monitor={monitor} />
      </td>
      <td className="ar-num">
        {share === null ? (
          <span className="ar-muted">No checks yet</span>
        ) : (
          /* The percentage alone, as drawn (parity P-SITES-03); how many checks
             it rests on is in the cell's title rather than a second line. */
          <span
            title={history.length === 1 ? 'From 1 check' : `From ${String(history.length)} checks`}
          >
            {formatShare(share)}
          </span>
        )}
      </td>
      <td className="ar-num ar-muted">
        {monitor.last_checked_at === null ? 'Never' : relativeTime(monitor.last_checked_at, now)}
        {monitor.last_latency_ms === null ? null : (
          <div className="app-sites__meta">{monitor.last_latency_ms} ms</div>
        )}
      </td>
      <td className="ar-muted">{describeIncident(history)}</td>
      {control}
    </tr>
  )
}

/**
 * Up, down, never checked, or paused — in that order of precedence.
 *
 * Paused wins over the state it was paused in, because a green dot beside a
 * monitor nobody is running says the site is up on evidence that stopped
 * arriving at an unknown point. `unknown` is a third state rather than an
 * optimistic green for the same reason `app/seo/uptime.State` makes it one.
 */
function StatusBadge({ monitor }: { monitor: Monitor }): JSX.Element {
  if (!monitor.enabled) return <span className="ar-badge ar-badge--neutral">Paused</span>
  if (monitor.state === 'up') return <span className="ar-badge ar-badge--success">● Up</span>
  if (monitor.state === 'down') {
    return (
      <>
        <span className="ar-badge ar-badge--danger">● Down</span>
        {monitor.down_for_seconds === null ? null : (
          <div className="app-sites__meta">for {describeOutage(monitor.down_for_seconds)}</div>
        )}
      </>
    )
  }
  return <span className="ar-badge ar-badge--neutral">Not checked yet</span>
}

/* --- the small conversions ------------------------------------------------------ */

/** How long a site has been down, in the coarsest unit that is still true. */
function describeOutage(seconds: number): string {
  if (seconds < 90) return 'under a minute'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${String(minutes)} min`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${String(hours)} hour${hours === 1 ? '' : 's'}`
  const days = Math.round(hours / 24)
  return `${String(days)} day${days === 1 ? '' : 's'}`
}

/** The share of the window's checks that answered, or `null` for no window. */
function uptimeShare(history: Check[]): number | null {
  if (history.length === 0) return null
  return (history.filter((check) => check.ok).length * 100) / history.length
}

/**
 * A percentage with one decimal, and none when it is whole.
 *
 * `99.7%` rather than `99.65517%`: the finest distinction 288 samples can draw
 * is a third of a percent, so further digits are precision the data does not
 * have. A clean `100%` keeps no decimal because `100.0%` reads as rounded.
 */
function formatShare(share: number): string {
  return Number.isInteger(share) ? `${String(share)}%` : `${share.toFixed(1)}%`
}

/**
 * The most recent failed check in the window, or the absence of one.
 *
 * Read from `history` rather than from `last_changed_at`, which is when the
 * *state* last flipped — that is the start of the current outage when a site is
 * down and the end of the last one when it is up, and neither is the thing a
 * column headed *Last incident* is asked for.
 */
function describeIncident(history: Check[]): string {
  const failed = history.find((check) => !check.ok)
  if (failed === undefined) return 'None in the last day'
  const when = formatDateTime(failed.checked_at, IST)
  const why =
    failed.error ??
    (failed.status_code === null ? 'no answer' : `${String(failed.status_code)} from origin`)
  return `${when} · ${why}`
}
